/**
 * @file 本だなの平積み (最近さわった本) の選び方を置く。
 *
 * @what: まなぶ人の学びメモ (直近 RECENT_NOTE_DAYS 日に更新したもの) が紐づく資料を
 *   メモの新しい順に並べ、その後ろにまなぶ人の閲覧位置 (最後に開いた本) を新しい順に
 *   つなぐ。同じ資料は先に出た方だけ残し、最大 RECENT_SHELF_LIMIT 冊にする。
 * @why: 本だなの上に「いま読んでいる本」を平積みのように並べ、役割のセクションから探さずに
 *   開けるようにする (代表の要望、2026-09-08)。メモを先にするのは、メモを付けた本は
 *   ただ開いた本より取り組みの途中である可能性が高いためである。
 * @failure: 純関数である。本だなに無い資料 id (消した本のメモ・閲覧位置) は落とす。
 */

import type { StudyNote } from "../api/notes";
import type { StudySource } from "../api/sources";
import type { ReadingPosition } from "../hooks/useReadingPositions";

/** 平積みに出す上限である。 */
export const RECENT_SHELF_LIMIT = 10;

/** 「直近」とみなすメモの更新の期間 (日)。 */
export const RECENT_NOTE_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * 平積みに並べる資料 (先頭が最も新しい)。
 *
 * @param notes まなぶ人のメモ (絞り込み済み。model/studyNoteContext.ts の notesForLearner)
 * @param positions まなぶ人の閲覧位置 (資料 id → ページと時刻)
 * @param now 判定の時刻 (epoch ms)
 */
export function recentShelfSources(
  sources: readonly StudySource[],
  notes: readonly StudyNote[],
  positions: ReadonlyMap<string, ReadingPosition>,
  now: number,
  limit: number = RECENT_SHELF_LIMIT,
): StudySource[] {
  const byId = new Map(sources.map((s) => [s.id, s]));
  const since = now - RECENT_NOTE_DAYS * DAY_MS;

  // 1段目は直近のメモが紐づく資料で、資料ごとに最も新しいメモの時刻で並べる
  const noteAt = new Map<string, number>();
  for (const n of notes) {
    const at = Date.parse(n.updated_at);
    if (!Number.isFinite(at) || at < since) continue;
    for (const ref of n.refs ?? []) {
      if (ref.kind !== "source" || !byId.has(ref.source_id)) continue;
      const prev = noteAt.get(ref.source_id);
      if (prev === undefined || at > prev) noteAt.set(ref.source_id, at);
    }
  }
  const byNote = [...noteAt.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);

  // 2段目は最後に開いた本 (新しい順)
  const byOpened = [...positions.entries()]
    .filter(([id]) => byId.has(id))
    .sort((a, b) => b[1].at - a[1].at)
    .map(([id]) => id);

  const out: StudySource[] = [];
  const seen = new Set<string>();
  for (const id of [...byNote, ...byOpened]) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(byId.get(id)!);
    if (out.length >= limit) break;
  }
  return out;
}
